import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { formatClinicDate, clinicTimeToUTC } from '../../utils/timezone';
import { useAvailabilityData } from '../../contexts/AvailabilityDataContext';
import { useAvailabilityUI } from '../../contexts/AvailabilityUIContext';
import { useAvailabilityCRUD, DeleteScope } from '../../hooks/useAvailabilityCRUD';
import { CreateMemberAvailabilityRequest } from '../../types/availability';
import { memberBookingAPI } from '../../services/api';
import { generateTimeOptions, formatTimeAMPM, formatDuration } from '../../utils/formatters';
import { Modal, Button } from '../ui';
import SelfBookingFields, {
  SelfBookingValues, defaultSelfBookingValues, clampSelfBooking, selfBookingEnd, validateSelfBooking,
  SelfBookingPolicyNote,
} from './SelfBookingFields';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

type Tab = 'hours' | 'reserve' | 'remove';

const selectClass =
  'w-full px-3 py-2 border rounded-lg bg-white text-clinic-dark focus:outline-none focus:ring-2 ' +
  'focus:ring-clinic-primary/30 focus:border-clinic-primary disabled:bg-clinic-surface-sunken disabled:text-clinic-muted';

/**
 * One calm dialog for a clicked availability block. Three tabs — Edit hours,
 * Reserve for me, Remove — so each action gets its own uncluttered view.
 * Booked blocks only offer Remove (the open time around the booking).
 */
const EditAvailabilityModal: React.FC = () => {
  const { state: dataState } = useAvailabilityData();
  const { state: uiState } = useAvailabilityUI();
  const { updateAvailability, deleteAvailability, closeAllModals, fetchWeeklyAvailability } = useAvailabilityCRUD();

  const block = uiState.modals.edit.block;
  const isBooked = !!block?.is_booked;
  const windowStart = block?.start_time.substring(0, 5) || '09:00';
  const windowEnd = block?.end_time.substring(0, 5) || '17:00';

  const [tab, setTab] = useState<Tab>(isBooked ? 'remove' : 'hours');
  const [loading, setLoading] = useState(false);

  // --- Edit hours ---------------------------------------------------------
  const [formData, setFormData] = useState<CreateMemberAvailabilityRequest>({
    date: block?.date || '', start_time: windowStart, end_time: windowEnd,
    recurrence_type: 'one_time', recurrence_end_date: null, notes: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // --- Reserve for me -----------------------------------------------------
  const [booking, setBooking] = useState<SelfBookingValues>(defaultSelfBookingValues(windowStart, windowEnd));
  const [confirming, setConfirming] = useState(false);
  const [clientEmailError, setClientEmailError] = useState<string | null>(null);

  // --- Remove -------------------------------------------------------------
  const [scope, setScope] = useState<DeleteScope>(isBooked ? 'day' : 'block');

  // Reset everything when a different block is opened.
  useEffect(() => {
    if (!block) return;
    const ws = block.start_time.substring(0, 5);
    const we = block.end_time.substring(0, 5);
    setTab(block.is_booked ? 'remove' : 'hours');
    setFormData({ date: block.date, start_time: ws, end_time: we, recurrence_type: 'one_time', recurrence_end_date: null, notes: null });
    setErrors({});
    setBooking(defaultSelfBookingValues(ws, we));
    setConfirming(false);
    setClientEmailError(null);
    setScope(block.is_booked ? 'day' : 'block');
  }, [block]);

  const globalHours = formData.date ? dataState.globalHours[formData.date] : null;
  const timeOptions = useMemo(() => {
    const start = globalHours?.start_time?.substring(0, 5) || '06:00';
    const end = globalHours?.end_time?.substring(0, 5) || '22:00';
    return generateTimeOptions(start, end);
  }, [globalHours]);

  if (!block) return null;

  const dateTitle = formatClinicDate(block.date, 'EEEE, MMM d');
  const rangeLabel = `${formatTimeAMPM(windowStart)} – ${formatTimeAMPM(windowEnd)}`;
  const statusLabel = !isBooked ? 'Available'
    : block.booking_type === 'pod' ? 'Pod booking'
    : block.booking_type === 'self' ? 'Reserved for you'
    : 'Client booking';
  const weekdayLabel = format(parseISO(block.date), 'EEEE');

  // ---- handlers ----------------------------------------------------------
  const handleUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!formData.date) next.date = 'Date is required';
    if (formData.start_time >= formData.end_time) next.end_time = 'End time must be after start time';
    if (globalHours) {
      if (formData.start_time < globalHours.start_time) next.start_time = `Clinic opens at ${formatTimeAMPM(globalHours.start_time)}`;
      if (formData.end_time > globalHours.end_time) next.end_time = `Clinic closes at ${formatTimeAMPM(globalHours.end_time)}`;
    }
    if (Object.keys(next).length) { setErrors(next); return; }
    setErrors({});
    setLoading(true);
    try {
      await updateAvailability(block.id, formData);
    } finally {
      setLoading(false);
    }
  };

  const handleReserve = async () => {
    const err = validateSelfBooking(booking);
    if (err) { setClientEmailError(err); return; }
    setClientEmailError(null);
    if (!confirming) { setConfirming(true); return; }

    setLoading(true);
    try {
      const [h, m] = booking.start.split(':').map(Number);
      await memberBookingAPI.createSelfBooking({
        start_time: clinicTimeToUTC(block.date, h, m),
        duration_minutes: booking.duration,
        num_attendees: Math.max(0, booking.groupSize - 1),
        client_name: booking.notifyClient ? booking.clientName.trim() || undefined : undefined,
        client_email: booking.notifyClient ? booking.clientEmail.trim() || undefined : undefined,
      });
      toast.success('Room reserved for your use');
      await fetchWeeklyAvailability();
      closeAllModals();
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to reserve room'));
      setConfirming(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    try {
      await deleteAvailability(block.id, scope);
    } finally {
      setLoading(false);
    }
  };

  // ---- pieces ------------------------------------------------------------
  const tabs: { id: Tab; label: string; hidden?: boolean }[] = [
    { id: 'hours', label: 'Edit hours', hidden: isBooked },
    { id: 'reserve', label: 'Reserve for me', hidden: isBooked },
    { id: 'remove', label: 'Remove' },
  ];

  const removeOptions: { value: DeleteScope; title: string; detail: string; hidden?: boolean }[] = [
    { value: 'block', title: 'Only this block', detail: `${rangeLabel} on ${formatClinicDate(block.date, 'MMM d')}.`, hidden: isBooked },
    { value: 'day', title: `Everything on ${formatClinicDate(block.date, 'EEE, MMM d')}`, detail: 'All open availability that day. Booked time stays.' },
    { value: 'series', title: `${rangeLabel} every ${weekdayLabel} going forward`, detail: 'This time slot on every upcoming week (next 6 months). Booked time stays.' },
  ];

  const footer = (() => {
    if (tab === 'hours') {
      return (
        <>
          <Button variant="ghost" onClick={closeAllModals} disabled={loading}>Cancel</Button>
          <Button type="submit" form="edit-hours-form" isLoading={loading} disabled={loading} data-guide="edit-hours-submit">
            Update hours
          </Button>
        </>
      );
    }
    if (tab === 'reserve') {
      return confirming ? (
        <>
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={loading}>Go back</Button>
          <Button onClick={handleReserve} isLoading={loading} disabled={loading} data-guide="selfbook-confirm">
            Confirm booking
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={closeAllModals} disabled={loading}>Cancel</Button>
          <Button onClick={handleReserve} disabled={loading} data-guide="selfbook-submit">
            Reserve {formatTimeAMPM(booking.start)} – {formatTimeAMPM(selfBookingEnd(booking))}
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="ghost" onClick={closeAllModals} disabled={loading}>Cancel</Button>
        <Button variant="danger" onClick={handleRemove} isLoading={loading} disabled={loading} data-guide="remove-submit">
          {scope === 'block' ? 'Delete this block' : scope === 'day' ? 'Clear this day' : `Clear every ${weekdayLabel}`}
        </Button>
      </>
    );
  })();

  return (
    <Modal
      isOpen
      onClose={() => { if (!loading) closeAllModals(); }}
      title={dateTitle}
      description={`${rangeLabel} · ${statusLabel}`}
      size="md"
      footer={footer}
    >
      {/* Segmented control */}
      <div className="flex rounded-lg bg-clinic-surface-sunken p-1 mb-5" role="tablist" aria-label="Actions">
        {tabs.filter(t => !t.hidden).map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => { setTab(t.id); setConfirming(false); }}
            disabled={loading}
            data-guide={`edit-tab-${t.id}`}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === t.id ? 'bg-white text-clinic-dark shadow-clinic-sm' : 'text-clinic-muted hover:text-clinic-dark'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hours' && (
        <form id="edit-hours-form" onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label htmlFor="edit-date" className="block text-sm font-medium text-clinic-dark mb-1">Date</label>
            <input
              id="edit-date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))}
              className={`${selectClass} ${errors.date ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
              disabled={loading}
            />
            {errors.date && <p className="mt-1 text-sm text-clinic-accent">{errors.date}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3" data-guide="edit-hours-times">
            <div>
              <label htmlFor="edit-start" className="block text-sm font-medium text-clinic-dark mb-1">Start</label>
              <select
                id="edit-start"
                value={formData.start_time}
                onChange={(e) => setFormData(p => ({ ...p, start_time: e.target.value }))}
                className={`${selectClass} ${errors.start_time ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                disabled={loading}
              >
                {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {errors.start_time && <p className="mt-1 text-sm text-clinic-accent">{errors.start_time}</p>}
            </div>
            <div>
              <label htmlFor="edit-end" className="block text-sm font-medium text-clinic-dark mb-1">End</label>
              <select
                id="edit-end"
                value={formData.end_time}
                onChange={(e) => setFormData(p => ({ ...p, end_time: e.target.value }))}
                className={`${selectClass} ${errors.end_time ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                disabled={loading}
              >
                {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {errors.end_time && <p className="mt-1 text-sm text-clinic-accent">{errors.end_time}</p>}
            </div>
          </div>
          <p className="text-xs text-clinic-muted">
            {globalHours
              ? `Clinic hours ${formatTimeAMPM(globalHours.start_time)} – ${formatTimeAMPM(globalHours.end_time)}. `
              : ''}
            Changes apply to this day only.
          </p>
        </form>
      )}

      {tab === 'reserve' && (
        confirming ? (
          <div className="space-y-3" data-guide="selfbook-confirm-summary">
            <p className="text-sm text-clinic-dark">
              Reserving a room on <strong>{formatClinicDate(block.date, 'EEE, MMM d')}</strong> from{' '}
              <strong>{formatTimeAMPM(booking.start)}</strong> to <strong>{formatTimeAMPM(selfBookingEnd(booking))}</strong>{' '}
              ({formatDuration(booking.duration)})
              {booking.groupSize > 1 && <> for <strong>{booking.groupSize} people</strong></>}.
            </p>
            {booking.notifyClient && booking.clientEmail.trim() && (
              <p className="text-sm text-clinic-dark">
                {booking.clientName.trim() || 'Your client'} (<strong>{booking.clientEmail.trim()}</strong>) will receive the
                confirmation and reminders by email.
              </p>
            )}
            <SelfBookingPolicyNote durationMinutes={booking.duration} />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-clinic-muted">
              Choose the exact time you need. Only that window is booked and billed.
            </p>
            <SelfBookingFields
              windowStart={windowStart}
              windowEnd={windowEnd}
              value={booking}
              onChange={(v) => { setBooking(clampSelfBooking(v, windowStart, windowEnd)); setClientEmailError(null); }}
              disabled={loading}
              clientEmailError={clientEmailError}
            />
          </div>
        )
      )}

      {tab === 'remove' && (
        <div className="space-y-3">
          {isBooked && (
            <p className="text-sm text-clinic-dark">
              This time is booked and can't be removed. You can still clear the open availability around it.
            </p>
          )}
          <div className="space-y-2" role="radiogroup" aria-label="What to remove" data-guide="remove-scope">
            {removeOptions.filter(o => !o.hidden).map(o => (
              <label
                key={o.value}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  scope === o.value ? 'border-clinic-accent bg-clinic-accent/5' : 'border-clinic-primary-lighter hover:bg-clinic-cream/50'
                }`}
              >
                <input
                  type="radio"
                  name="remove-scope"
                  value={o.value}
                  checked={scope === o.value}
                  onChange={() => setScope(o.value)}
                  disabled={loading}
                  className="mt-1 h-4 w-4 text-clinic-accent border-clinic-primary-light focus:ring-clinic-accent/30"
                />
                <span>
                  <span className="block text-sm font-medium text-clinic-dark">{o.title}</span>
                  <span className="block text-xs text-clinic-muted">{o.detail}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="text-xs text-clinic-muted">This can't be undone.</p>
        </div>
      )}
    </Modal>
  );
};

export default EditAvailabilityModal;
